import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useListProvidersApiV1ProvidersGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { US_STATES, US_TIMEZONES } from '#/shared/config/geo'
import type { MultiSelectOption } from '#/shared/ui/multi-select'

// The providers endpoint caps `size` at 100, which comfortably covers the
// source list; the multi-select filters the loaded set client-side.
const PROVIDER_PAGE_SIZE = 100

export interface ShipmentFilterOptions {
  states: MultiSelectOption[]
  timezones: MultiSelectOption[]
  sources: MultiSelectOption[]
  sourcesLoading: boolean
}

/**
 * Option lists for the shipment list's multi-value filters.
 *
 * States and time zones are static reference data. Sources come from the
 * providers endpoint, so the query is gated: the filter popover lives in an
 * always-mounted toolbar, and fetching every provider on every list render
 * would be wasted work. `enabled` should be true while the popover is open —
 * or while a Sources filter is applied, since the chip row needs the provider
 * names to label the selected ids.
 */
export function useShipmentFilterOptions(
  enabled: boolean,
): ShipmentFilterOptions {
  const { t } = useTranslation()

  const providersQuery = useListProvidersApiV1ProvidersGet(
    { page: 1, size: PROVIDER_PAGE_SIZE },
    { query: { enabled } },
  )

  const states = useMemo<MultiSelectOption[]>(
    () => US_STATES.map((state) => ({ value: state.code, label: state.name })),
    [],
  )

  const timezones = useMemo<MultiSelectOption[]>(
    () =>
      US_TIMEZONES.map((zone) => ({
        value: zone.value,
        label: t(zone.labelI18nKey),
      })),
    [t],
  )

  const providerItems = providersQuery.data?.data.items
  const sources = useMemo<MultiSelectOption[]>(
    () =>
      (providerItems ?? []).map((provider) => ({
        value: provider.id,
        label: provider.name,
      })),
    [providerItems],
  )

  return {
    states,
    timezones,
    sources,
    sourcesLoading: enabled && providersQuery.isPending,
  }
}
